import { useEffect, useId, useState } from 'react';

import { prefersReducedMotion } from '../reducedMotion';

/**
 * How far a parent has come with its subtasks: done of total, as a ring
 * (spec 153 FR-003, FR-004).
 *
 * The arc wears the brand's progress gradient under waiver WD-07: subtask
 * progress is a true ratio, the surface FD-16's gradient was reserved for
 * and never had (OD-19). The stops are the `--cg-progress` stops. In dark mode
 * the arc starts at Capgemini Blue rather than the navy the card itself is
 * drawn in, so its start does not vanish into the surface.
 *
 * Named as a sentence for assistive technology ("1 of 4 subtasks done"); the
 * figure inside is decoration for sighted readers of the same fact.
 *
 * It draws in once when first shown (spec 155 FR-013): the arc starts empty
 * and grows to its value on the next frame. Later values move as any change
 * would. Under reduced motion it is drawn at its value from the start.
 */
export function ProgressRing({
  done,
  total,
  size = 44,
}: {
  done: number;
  total: number;
  size?: number;
}) {
  const id = useId();
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const share = total > 0 ? Math.min(done / total, 1) : 0;
  const [drawn, setDrawn] = useState(prefersReducedMotion);
  useEffect(() => {
    if (drawn) return;
    const frame = requestAnimationFrame(() => {
      setDrawn(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [drawn]);

  return (
    <svg
      role="img"
      aria-label={`${String(done)} of ${String(total)} subtasks done`}
      viewBox="0 0 44 44"
      width={size}
      height={size}
      className="shrink-0"
    >
      <defs>
        <linearGradient id={id} x1="0" x2="1" y1="0" y2="0">
          <stop
            offset="0%"
            className="[stop-color:var(--progress-stop-start)]"
          />
          <stop
            offset="30%"
            className="[stop-color:var(--progress-stop-blue)]"
          />
          <stop
            offset="70%"
            className="[stop-color:var(--progress-stop-light)]"
          />
          <stop
            offset="100%"
            className="[stop-color:var(--progress-stop-end)]"
          />
        </linearGradient>
      </defs>
      <circle
        cx="22"
        cy="22"
        r={radius}
        fill="none"
        strokeWidth="5"
        className="stroke-muted"
      />
      {share > 0 ? (
        <circle
          cx="22"
          cy="22"
          r={radius}
          fill="none"
          strokeWidth="5"
          strokeLinecap="round"
          stroke={`url(#${id})`}
          strokeDasharray={`${String(drawn ? circumference * share : 0)} ${String(circumference)}`}
          data-ring-arc
          className="motion-safe:ease-standard motion-safe:transition-[stroke-dasharray] motion-safe:duration-slow"
          transform="rotate(-90 22 22)"
        />
      ) : null}
      <text
        x="22"
        y="26"
        textAnchor="middle"
        aria-hidden="true"
        className="fill-foreground text-2xs font-mono font-bold"
      >
        {done}/{total}
      </text>
    </svg>
  );
}
